<template>
  <div
    :id="randId + resultId"
    class="lite"
    :style="{ height: height + 'px' }"
  />
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, GraphicComponent, LegendComponent, DataZoomComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import theme from '@/components/chart/theme.json'
import helperMethods from '@/helpers/utility.js'
echarts.use([TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, GraphicComponent, LegendComponent, DataZoomComponent, BarChart, LineChart, PieChart, CanvasRenderer])

export default {
  props: {
    resultId: {
      type: [Boolean, String],
      default: ''
    },
    data: {
      type: Object
    },
    licensed: {
      type: Boolean,
      default: false
    },
    height: {
      type: [Number, String],
      default: ''
    }
  },
  data () {
    return {
      randId: (Math.random() + 1).toString(36).substring(2) + '_',
      chart: false,
      formattedData: {},
      firstTimeline: '',
      themeJson: theme
    }
  },
  computed: {
    isAuthenticated () {
      return this.$store.getters['user/isAuthenticated']
    },
    /*selectedControls () {
      return this.$store.getters['search/getSelectedControls']
    },*/
    defaultChart () {
      return this.getDefaultChart(this.data)
    },
    gridSize () {
      let settings = this.$store.getters['app/getSettings']
      return settings.datasets.display
    }
  },
  mounted() {
    if (this.firstTimeline === '') {
      let formattedKeys = []
      let keys = []

      Object.entries(this.data).forEach(row => {
        let [key, value] = row
        formattedKeys = formattedKeys.concat(Object.keys(value))
        keys = [... new Set(formattedKeys)]
      })

      this.firstTimeline = keys[0]
    }

    this.formatData()
    this.init()

    window.addEventListener('resize', e => {
      let that = this

      this.$nextTick(() => {
        setTimeout(function () {
          that.chart.resize()
        }, 250)
      })
    })
  },
  unmounted() {
    window.removeEventListener('resize')
    //this.chart.dispose()
  },
  watch: {
    gridSize: function () {
      let that = this

      this.$nextTick(() => {
        setTimeout(function () {
          that.chart.resize()
        }, 0)
      })
    }
  },
  methods: {
    filteredData (key, value) {
      let result = []

      for (const [key, value] of Object.entries(this.data)) {
        result.push({ 'name': key, 'value': this.data[key][this.firstTimeline] })
      }

      return result
    },
    formatData() {
      let _this = this
      let series = {}
      let formattedSeries = []
      let formattedKeys = []

      Object.entries(this.data).forEach(row => {
        let [key, value] = row

        if (this.defaultChart.type === 'pie') {
          series = {
            name: this.firstTimeline,
            type: 'pie',
            radius: '60%',
            center: ['50%', '47.5%'],
            data: this.filteredData(),
            itemStyle: {
              borderWidth: 2,
              borderColor: 'rgb(27, 30, 33)',
            },
            label: {
              show: false
            },
            labelLayout: {
              hideOverlap: true
            },
            emphasis: {
              itemStyle: {
                
              }
            }
          }
        } else {
          series = {
            name: key,
            type: this.defaultChart.type === 'bar' ? 'bar' : 'line',
            data: Object.values(value),
            stack: this.defaultChart.stack,
            barMaxWidth: '75',
            itemStyle: {
              borderWidth: 0
            },
            symbol: 'circle',
            symbolSize: 9,
            lineStyle: {
              width: 4
            },
            label: {
              show: false
            }
          }
        }

        formattedSeries.push(series)
        formattedKeys = formattedKeys.concat(Object.keys(value))
      })

      this.formattedData = {
        series: formattedSeries,
        keys: [... new Set(formattedKeys)]
      }
    },
    init() {
      let _this = this

      if (Object.keys(this.chart).length === 0) {
        let element = document.getElementById(this.randId + this.resultId)

        if (element !== null) {
          echarts.registerTheme('statzon', this.themeJson)
          this.chart = echarts.init(document.getElementById(this.randId + this.resultId), 'statzon')

          let option = {
            textStyle: {
              color: 'rgba(255,255,255,0.8)'
            },
            grid: {
              left: '0%',
              right: this.defaultChart.axis === 'yAxis' ? '4' : '16',
              top: '10',
              bottom: '6',
              containLabel: true
            },
            graphic: {
              elements: [
                {
                  type: 'image',
                  id: 'logo',
                  left: 'center',
                  top: '30px',
                  z: -1,
                  bounding: 'raw',
                  style: {
                    image: '/img/statzon_logo_white.svg',
                    width: 150,
                    opacity: 0.02
                  }
                }
              ]
            },
            yAxis: this.defaultChart.axis === 'yAxis' ? {
              show: this.defaultChart.type === 'pie' ? false : true,
              type: 'value',
              axisLabel: {
                formatter: function (value, index) {
                  return _this.licensed ? _this.formatNumber(value, 1) : '??'
                }
              }
            } : {
              show: this.defaultChart.type === 'pie' ? false : true,
              type: 'category',
              data: this.formattedData.keys,
              axisLabel: {
                formatter: function (value, index) {
                  return value.length > 13 ? value.slice(0, 10) + '...' : value
                }
              }
            },
            xAxis: this.defaultChart.axis === 'xAxis' ? {
              show: this.defaultChart.type === 'pie' ? false : true,
              type: 'value',
              axisLabel: {
                formatter: function (value, index) {
                  return _this.isAuthenticated ? _this.formatNumber(value, 1) : '??'
                }
              }
            } : {
              show: this.defaultChart.type === 'pie' ? false : true,
              type: 'category',
              data: this.formattedData.keys
            },
            avoidLabelOverlap: true,
            stillShowZeroSum: false,
            series: this.formattedData.series
          }

          this.chart.setOption(option)
        }
      }
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.lite {
  width: 100%;
  height: 172px; // TODO: FIX THIS
}

</style>
